import { useState, useEffect } from 'react';
import { Step1StudentInfo } from './components/Step1StudentInfo';
import { Step2Appointment } from './components/Step2Appointment';
import { Step3Confirmation } from './components/Step3Confirmation';
import { ProgressIndicator } from './components/ProgressIndicator';
import { useAuth } from './hooks/useAuth';
import { interviewService } from './services/interview.service';
import type { UserData } from './types/api.types';

export interface FormData {
  // Student Information
  firstName: string;
  lastName: string;
  desiredGrade: string;
  nationalId: string;
  dateOfBirth: string;
  placeOfBirth: string;
  homeAddress: string;
  
  // Parent Information
  fatherName: string;
  fatherPhone: string;
  motherPhone: string;
  fatherJob: string;
  fatherEducation: string;
  motherEducation: string;
  
  // Appointment
  selectedDate: string;
  selectedTime: string;
}

export default function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    desiredGrade: '',
    nationalId: '',
    dateOfBirth: '',
    placeOfBirth: '',
    homeAddress: '',
    fatherName: '',
    fatherPhone: '',
    motherPhone: '',
    fatherJob: '',
    fatherEducation: '',
    motherEducation: '',
    selectedDate: '',
    selectedTime: '',
  });
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [userError, setUserError] = useState<string | null>(null);
  const [_situationId, setSituationId] = useState<number | null>(null);
  
  const { isAuthenticated: _, isLoading: isAuthLoading, error: _authError } = useAuth();

  // Fetch user data (works with or without authentication)
  useEffect(() => {
    if (!isAuthLoading) {
      fetchUserData();
    }
  }, [isAuthLoading]);

  const fetchUserData = async () => {
    try {
      setIsLoadingUser(true);
      console.log('Fetching user data...');
      const data = await interviewService.getUserData();
      console.log('User data received:', data);
      console.log('Detected User ID:', data.user_id);
      setUserData(data);
      
      // Pre-fill form with user data if user_id is not 0 (authenticated user)
      if (data.user_id && data.user_id !== 0) {
        console.log('Pre-filling form with user data');
        setFormData(prev => ({
          ...prev,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          desiredGrade: data.desiredGrade || '',
          nationalId: data.nationalId || '',
          dateOfBirth: data.dateOfBirth || '',
          placeOfBirth: data.placeOfBirth || '',
          homeAddress: data.homeAddress || '',
          fatherName: data.fatherName || '',
          fatherPhone: data.fatherPhone || '',
          motherPhone: data.motherPhone || '',
          fatherJob: data.fatherJob || '',
          fatherEducation: data.fatherEducation || '',
          motherEducation: data.motherEducation || '',
        }));
      } else {
        console.log('Anonymous user - form will be empty');
      }
      
      setUserError(null);
    } catch (error: any) {
      console.error('Error fetching user data:', error);
      setUserError(error.message || 'خطا در دریافت اطلاعات کاربر');
    } finally {
      setIsLoadingUser(false);
    }
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleUpdateFormData = (data: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  // Auto-scroll to top when navigating to Step 3
  useEffect(() => {
    if (currentStep === 3) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  // Show loading state
  if (isAuthLoading || isLoadingUser) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--primary-gold)' }}></div>
          <p className="text-lg" style={{ color: 'var(--text-gray)' }}>
            در حال بارگذاری...
          </p>
        </div>
      </div>
    );
  }

  // Show user data fetch error (only if critical error, not for missing auth)
  if (userError) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-2xl shadow-lg p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h2 className="text-2xl mb-4" style={{ color: 'var(--secondary-green)' }}>
            خطا در دریافت اطلاعات
          </h2>
          <p className="text-lg mb-6" style={{ color: 'var(--text-gray)' }}>
            {userError}
          </p>
          <button
            onClick={fetchUserData}
            className="inline-block px-6 py-3 rounded-xl"
            style={{
              backgroundColor: 'var(--primary-gold)',
              color: 'var(--secondary-green)',
            }}
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl mb-2" style={{ color: 'var(--secondary-green)' }}>
            پیش‌ثبت‌نام مدرسه بنیان
          </h1>
          <p className="text-lg" style={{ color: 'var(--text-gray)' }}>
            ثبت‌نام فرزند خود را در ۳ مرحله ساده تکمیل کنید
          </p>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator currentStep={currentStep} />

        {/* Step Content */}
        <div className="mt-8">
          {currentStep === 1 && (
            <Step1StudentInfo
              formData={formData}
              onUpdate={handleUpdateFormData}
              onNext={handleNext}
              onSituationCreated={setSituationId}
              userId={userData?.user_id || 0}
            />
          )}
          {currentStep === 2 && (
            <Step2Appointment
              formData={formData}
              onUpdate={handleUpdateFormData}
              onNext={handleNext}
              onBack={handleBack}
              userId={userData?.user_id || 0}
            />
          )}
          {currentStep === 3 && (
            <Step3Confirmation
              formData={formData}
              onBack={handleBack}
              onSubmit={() => {}}
              userId={userData?.user_id || 0}
            />
          )}
        </div>
      </div>
    </div>
  );
}